import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Modal, Form, Input, Select, Spin, Popconfirm } from 'antd';
import { UserPlus, Edit2, Trash2, Link2, ShieldCheck } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { USERS, ROLES, PEOPLE } from '../../constants/api';
import { DEPARTMENT_LABELS } from '../../constants/enum';
import ToastMsg from '../../components/common/ToastMsg';

const UsersManager = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const loadData = async () => {
    const [usersRes, rolesRes, peopleRes] = await Promise.all([
      fetch(USERS, {}, setLoading),
      fetch(ROLES),
      fetch(PEOPLE),
    ]);
    if (usersRes?.status !== 'fail') {
      const raw = usersRes?.data;
      setUsers(Array.isArray(raw) ? raw : raw?.users || raw?.results || []);
    }
    if (rolesRes?.status !== 'fail') {
      const rawRoles = rolesRes?.data;
      setRoles(Array.isArray(rawRoles) ? rawRoles : rawRoles?.roles || rawRoles?.results || []);
    }
    if (peopleRes?.status === 'success') {
      const p = peopleRes.data || [];
      setPeople(Array.isArray(p) ? p : []);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IDs of employees already mapped to other users (exclude current editing user)
  const mappedEmployeeIds = users
    .filter((u) => u.employee_id && (!editingUser || u.id !== editingUser.id))
    .map((u) => u.employee_id);

  const peopleOptions = people.map((p) => ({
    value: p.id,
    label: p.name,
    disabled: mappedEmployeeIds.includes(p.id),
  }));

  const roleOptions = roles.map((r) => ({
    value: r.id,
    label: r.is_admin ? `${r.name} (Admin)` : r.name,
  }));

  const handleSave = async (values) => {
    const payload = {};
    const allowedFields = [
      'login_name', 'login_fname', 'login_lname', 'login_email', 'login_phone',
      'login_password', 'department', 'assigned_role_id', 'employee_id',
    ];
    for (const key of allowedFields) {
      if (key in values && values[key] !== undefined) payload[key] = values[key];
    }
    // Don't send blank password on edit
    if (editingUser && !payload.login_password) delete payload.login_password;

    let res;
    if (editingUser) {
      res = await update(`${USERS}${editingUser.id}/`, payload);
    } else {
      res = await create(USERS, payload);
    }

    if (res?.status !== 'fail') {
      ToastMsg('success', editingUser ? 'User updated' : 'User created');
      setModalOpen(false);
      setEditingUser(null);
      form.resetFields();
      loadData();
    } else {
      ToastMsg('error', res?.message || 'Failed to save user');
    }
  };

  const handleDelete = async (id) => {
    const res = await remove(`${USERS}${id}/`);
    if (res?.status !== 'fail') {
      ToastMsg('success', 'User deleted');
      loadData();
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalOpen(true);
  };

  const columns = [
    { title: 'Username', dataIndex: 'login_name', key: 'login_name' },
    { title: 'First Name', dataIndex: 'login_fname', key: 'login_fname', render: (v) => v || '-' },
    { title: 'Last Name', dataIndex: 'login_lname', key: 'login_lname', render: (v) => v || '-' },
    { title: 'Email', dataIndex: 'login_email', key: 'login_email', render: (v) => v || '-' },
    {
      title: 'Employee', dataIndex: 'employee_name', key: 'employee_name',
      render: (v) => v ? (
        <span className="flex items-center gap-1 text-sm">
          <Link2 size={12} className="text-indigo-500" />{v}
        </span>
      ) : <span className="text-muted-foreground text-xs">ERP only</span>,
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (val, r) => val ? (
        <Tag color={r.is_admin ? 'purple' : 'blue'} icon={r.is_admin ? <ShieldCheck size={12} /> : null}>
          {val}
        </Tag>
      ) : <span className="text-muted-foreground text-xs">No role</span>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (val) => DEPARTMENT_LABELS[val] || val || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            size="small"
            icon={<Edit2 size={14} />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={14} />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <p className="text-muted-foreground text-sm">Manage system users and their access</p>
        </div>
        <Button
          type="primary"
          icon={<UserPlus size={16} />}
          onClick={openCreate}
          style={{ backgroundColor: '#4f46e5' }}
        >
          Add User
        </Button>
      </div>

      <Card className="rounded-xl shadow-sm">
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Create User'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingUser(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editingUser ? 'Update' : 'Create'}
        okButtonProps={{ style: { backgroundColor: '#4f46e5' } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="login_name"
            label="Username"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input disabled={!!editingUser} placeholder="e.g. jsmith" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="login_fname" label="First Name">
              <Input />
            </Form.Item>
            <Form.Item name="login_lname" label="Last Name">
              <Input />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="login_email"
              label="Email"
              rules={[{ type: 'email', message: 'Invalid email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="login_phone" label="Phone">
              <Input />
            </Form.Item>
          </div>
          <Form.Item
            name="login_password"
            label={editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
            rules={editingUser ? [] : [{ required: true, message: 'Required' }, { min: 6, message: 'Min 6 characters' }]}
            extra={editingUser ? 'Leave blank to keep the existing password.' : ''}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item
            name="employee_id"
            label="Linked Employee"
            extra="Map this user to an employee from People. Leave empty for ERP-only users."
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={peopleOptions}
              placeholder="Select employee (optional)"
              allowClear
            />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="assigned_role_id" label="Role" rules={[{ required: true, message: 'Select a role' }]}>
              <Select
                options={roleOptions}
                placeholder="Select role"
                allowClear
              />
            </Form.Item>
            <Form.Item name="department" label="Department">
              <Select
                options={Object.entries(DEPARTMENT_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
                placeholder="Select department"
                allowClear
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersManager;
